class EditorialCountStats extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
    this.observer = null;
  }

  connectedCallback() {
    if (this.hasAnimated) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFinalValues();
      this.hasAnimated = true;
      return;
    }

    this.showInitialValues();

    if (!('IntersectionObserver' in window)) {
      this.animate();
      return;
    }

    const threshold = Number.parseFloat(this.dataset.threshold || '0.35');
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.animate();
            this.observer?.disconnect();
          }
        });
      },
      {
        root: null,
        threshold,
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get counters() {
    return [...this.querySelectorAll('[data-count-number]')];
  }

  showInitialValues() {
    this.counters.forEach((counter) => {
      const parsed = this.parseValue(counter.dataset.target);
      counter.textContent = this.formatValue(0, parsed.decimals, counter.dataset.format === 'true');
    });
  }

  showFinalValues() {
    this.counters.forEach((counter) => {
      const parsed = this.parseValue(counter.dataset.target);
      counter.textContent = this.formatValue(parsed.target, parsed.decimals, counter.dataset.format === 'true');
    });
  }

  animate() {
    if (this.hasAnimated) return;
    this.hasAnimated = true;

    const duration = Number.parseInt(this.dataset.duration || '1600', 10);

    this.counters.forEach((counter) => {
      const parsed = this.parseValue(counter.dataset.target);
      const shouldFormat = counter.dataset.format === 'true';
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = parsed.target * eased;

        counter.textContent = this.formatValue(current, parsed.decimals, shouldFormat);

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          counter.textContent = this.formatValue(parsed.target, parsed.decimals, shouldFormat);
        }
      };

      counter.textContent = this.formatValue(0, parsed.decimals, shouldFormat);
      requestAnimationFrame(tick);
    });
  }

  parseValue(value) {
    const normalized = String(value || '0').replace(/,/g, '').trim();
    const target = Number.parseFloat(normalized);
    const decimalPart = normalized.split('.')[1];

    return {
      target: Number.isFinite(target) ? target : 0,
      decimals: decimalPart ? decimalPart.length : 0,
    };
  }

  formatValue(value, decimals, shouldFormat) {
    const safeValue = Number.isFinite(value) ? value : 0;

    if (shouldFormat) {
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(safeValue);
    }

    return safeValue.toFixed(decimals);
  }
}

if (!customElements.get('editorial-count-stats')) {
  customElements.define('editorial-count-stats', EditorialCountStats);
}
